<script setup lang="ts">
type SurveySwiperInfo = {
  title?: string
  images?: string[]
  isvideo?: boolean
  videocover?: string
  vcover?: string
  vurl?: string
  isvr?: boolean | string
  vr?: string
  vrm?: {
    code?: string
    url?: string
    cover?: string
  }
}

/** 组件入参，包含轮播区需要的图片、视频和 VR 数据。 */
const props = withDefaults(
  defineProps<{
    info?: SurveySwiperInfo
  }>(),
  {
    info: () => ({}),
  },
)

/** 向父组件抛出弹窗打开等交互事件。 */
const emit = defineEmits<{
  openVideoDialog: [url: string]
  openVrDialog: [vrm: SurveySwiperInfo['vrm']]
  close: []
}>()

const { t } = useAppI18n()
const { isRtl } = useRtl()

/** 顶部 VR 入口图标地址。 */
const VR_ENTRY_ICON = 'https://resources1.interface003.com/web30/2026/06/11/2a489d86-1e3b-4f45-a189-29af64a20e6b.gif'
/** 顶部视频入口图标地址。 */
const VIDEO_ENTRY_ICON = 'https://resources1.interface003.com/web30/2026/06/11/802c7336-408c-478c-b76e-7036f49c8cd4.png'
/** 左侧上一张按钮图标地址。 */
const LEFT_ICON = 'https://resources1.interface003.com/web30/2026/06/11/b20f9767-20ee-4d76-b98f-927eb724c8f6.png'
/** 右侧下一张按钮图标地址。 */
const RIGHT_ICON = 'https://resources1.interface003.com/web30/2026/06/11/f4122ad0-2700-484d-85e5-7fc2ce51220a.png'
/** 顶部视频标签图标地址。 */
const TOP_VIDEO_ICON = 'https://resources1.interface003.com/web30/2026/06/11/30f23b30-4b58-41d4-9614-7e6b124af177.png'
/** 顶部图片标签图标地址。 */
const TOP_IMAGE_ICON = 'https://resources1.interface003.com/web30/2026/06/11/bc7ec7c3-3dc0-479c-b52e-6482f6efa504.png'
/** 底部上一项按钮图标地址。 */
const THUMB_LEFT_ICON = 'https://resources1.interface003.com/web30/2026/06/11/5b6054c3-7970-47fe-a3cb-c50f0622f59f.png'
/** 缩略图视频遮罩图标地址。 */
const THUMB_VIDEO_ICON = 'https://resources1.interface003.com/web30/2026/06/11/000f5ac4-e492-4b1b-85aa-d29e16e6b46b.png'
/** 缩略图 VR 遮罩图标地址。 */
const THUMB_VR_ICON = 'https://resources1.interface003.com/web30/2026/06/11/6b45310e-a031-4f9a-9504-cab0c20a5fde.png'
/** 缩略图图片遮罩图标地址。 */
const THUMB_IMAGE_ICON = 'https://resources1.interface003.com/web30/2026/06/24/de64f72c-bebb-4ac4-b809-450789378bfb.png'
/** 底部下一项按钮图标地址。 */
const THUMB_RIGHT_ICON = 'https://resources1.interface003.com/web30/2026/06/11/3e467585-ab30-4c30-bcf9-52c65ab4730a.png'

/** 图片列表，提前过滤掉空地址。 */
const imageList = computed(() => props.info?.images?.filter(Boolean) ?? [])
/** 当前实勘是否具备 VR 入口。 */
const hasVr = computed(() => Boolean(props.info?.vr && props.info?.vrm?.code))
/** 当前实勘是否具备视频入口。 */
const hasVideo = computed(() => Boolean(props.info?.vurl && props.info?.vcover))

type DisplayItem = {
  src: string
  kind: 'vr' | 'video' | 'image'
}

/** 主轮播数据源，按 VR、视频、图片顺序拼装。 */
const displayList = computed<DisplayItem[]>(() => {
  const list: DisplayItem[] = []

  if (hasVr.value && props.info?.vrm?.cover) {
    list.push({
      src: props.info.vrm.cover,
      kind: 'vr',
    })
  }

  if (hasVideo.value && props.info?.vcover) {
    list.push({
      src: props.info.vcover,
      kind: 'video',
    })
  }

  list.push(
    ...imageList.value.map<DisplayItem>((src) => ({
      src,
      kind: 'image',
    })),
  )

  return list
})

/** 当前激活的主轮播索引。 */
const currentIndex = ref(0)
/** 缩略图 DOM 引用列表，用于自动滚动定位。 */
const thumbRefs = ref<HTMLElement[]>([])

/** 当前主图地址，同时替换为大图规格。 */
const bigImageSrc = computed(() => {
  const src = displayList.value[currentIndex.value]?.src ?? ''
  return src.replace('_stock', '_bit800')
})

/** 当前激活的展示项。 */
const currentDisplayItem = computed(() => displayList.value[currentIndex.value] ?? null)

/** 当前激活项的类型。 */
const currentDisplayKind = computed(() => currentDisplayItem.value?.kind ?? 'image')

/** 仅包含图片类型的展示列表，用于计数和大图预览。 */
const imageDisplayList = computed(() => displayList.value.filter((item) => item.kind === 'image'))

/** 缩略图展示列表，保持原始索引顺序，RTL 下由页面 direction 自然从右向左排列。 */
const thumbDisplayList = computed(() => displayList.value)

/**
 * 将缩略图展示索引映射回主轮播索引。
 * @param thumbIndex 缩略图展示索引。
 * @returns 主轮播索引。
 */
const getDisplayIndexByThumbIndex = (thumbIndex: number) => {
  if (thumbIndex < 0 || thumbIndex >= thumbDisplayList.value.length) {
    return 0
  }

  return thumbIndex
}

/**
 * 将主轮播索引映射到缩略图展示索引。
 * @param displayIndex 主轮播索引。
 * @returns 缩略图展示索引。
 */
const getThumbIndexByDisplayIndex = (displayIndex: number) => {
  if (displayIndex < 0 || displayIndex >= displayList.value.length) {
    return 0
  }

  return displayIndex
}

/**
 * 将轮播索引映射为图片列表索引。
 * @param displayIndex 轮播区展示项索引。
 * @returns 对应的图片索引，非图片项时返回 0。
 */
const getImageIndexByDisplayIndex = (displayIndex: number) => {
  const currentItem = displayList.value[displayIndex]
  if (!currentItem) {
    return 0
  }

  if (currentItem.kind !== 'image') {
    return 0
  }

  const imageIndex = imageDisplayList.value.findIndex((item) => item.src === currentItem.src)

  return imageIndex >= 0 ? imageIndex : 0
}

/**
 * 获取第一张图片在主轮播中的索引。
 * @returns 第一张图片对应的轮播索引，无图片时返回 0。
 */
const getFirstImageDisplayIndex = () => {
  const firstImageIndex = displayList.value.findIndex((item) => item.kind === 'image')

  return firstImageIndex >= 0 ? firstImageIndex : 0
}

/** 底部图片计数文案，仅统计图片项。 */
const currentCount = computed(() => {
  const total = imageDisplayList.value.length || 1
  return `${getImageIndexByDisplayIndex(currentIndex.value) + 1}/${total}`
})

/**
 * 生成缩略图规格地址。
 * @param src 原图地址。
 * @returns 替换后的缩略图地址。
 */
const thumbImageSrc = (src: string) => src.replace('_stock', '_survey3175')

/** 将当前激活缩略图滚动到可视区域中。 */
const syncThumbVisibility = async () => {
  await nextTick()

  const thumb = thumbRefs.value[getThumbIndexByDisplayIndex(currentIndex.value)]
  if (!thumb) {
    return
  }

  thumb.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
    inline: 'center',
  })
}

/**
 * 设置当前轮播索引并同步缩略图位置。
 * @param index 目标索引，支持越界循环。
 * @returns Promise<void>
 */
const setCurrentIndex = async (index: number) => {
  const maxIndex = displayList.value.length - 1
  if (maxIndex < 0) {
    currentIndex.value = 0
    return
  }

  currentIndex.value = (index + displayList.value.length) % displayList.value.length
  await syncThumbVisibility()
}

/**
 * 点击缩略图切换主轮播。
 * @param index 缩略图索引。
 * @returns Promise<void>
 */
const selectThumb = async (index: number) => {
  await setCurrentIndex(getDisplayIndexByThumbIndex(index))
}

/** 切换到第一张图片对应的展示项。 */
const selectImageCount = async () => {
  if (!imageDisplayList.value.length) {
    return
  }

  await setCurrentIndex(getFirstImageDisplayIndex())
}

/**
 * 按资源类型切换当前轮播项。
 * @param kind 目标展示类型。
 * @returns Promise<void>
 */
const selectKind = async (kind: DisplayItem['kind']) => {
  const targetIndex = displayList.value.findIndex((item) => item.kind === kind)

  if (targetIndex < 0) {
    if (kind === 'image') {
      await selectImageCount()
    }

    return
  }

  await setCurrentIndex(targetIndex)
}

/**
 * 计算步进后的索引，供上一张/下一张操作复用。
 * @param delta 步进值，正数向后，负数向前。
 * @returns 循环后的有效索引。
 */
const stepIndex = (delta: number) => {
  const nextIndex = currentIndex.value + delta
  return (nextIndex + displayList.value.length) % displayList.value.length
}

/** 切换到上一项。 */
const prevImg = async () => {
  if (displayList.value.length <= 1) {
    return
  }

  await setCurrentIndex(stepIndex(isRtl.value ? 1 : -1))
}

/** 切换到下一项。 */
const nextImg = async () => {
  if (displayList.value.length <= 1) {
    return
  }

  await setCurrentIndex(stepIndex(isRtl.value ? -1 : 1))
}

/** 打开视频弹窗并透传视频地址。 */
const openVideoDialog = () => {
  emit('openVideoDialog', props.info?.vurl || '')
}

/** 打开 VR 弹窗并透传 VR 信息。 */
const openVrDialog = () => {
  emit('openVrDialog', props.info?.vrm)
}

/**
 * 获取缩略图遮罩类型。
 * @param index 缩略图索引。
 * @returns 当前缩略图对应的资源类型。
 */
const getThumbMaskKind = (index: number) => displayList.value[getDisplayIndexByThumbIndex(index)]?.kind ?? 'image'

/**
 * 判断顶部快捷入口是否处于激活状态。
 * @param kind 顶部入口类型。
 * @returns 是否激活。
 */
const isTopOptionActive = (kind: DisplayItem['kind']) => {
  if (kind === 'image') {
    return currentDisplayKind.value === 'image'
  }

  return currentDisplayKind.value === kind
}

/**
 * 生成顶部快捷入口样式类名。
 * @param kind 顶部入口类型。
 * @returns 对应的类名字符串。
 */
const getTopOptionClass = (kind: DisplayItem['kind']) =>
  isTopOptionActive(kind) ? 'bg-[#112D73]' : 'bg-[rgba(5,12,31,0.46)] hover:bg-[rgba(5,12,31,0.62)]'

/**
 * 判断缩略图是否为当前激活项。
 * @param index 缩略图索引。
 * @returns 是否激活。
 */
const isThumbActive = (index: number) => {
  const displayIndex = getDisplayIndexByThumbIndex(index)

  if (displayIndex === currentIndex.value) {
    return true
  }

  const currentItem = displayList.value[currentIndex.value]
  const thumbItem = displayList.value[displayIndex]

  return Boolean(
    currentItem &&
    thumbItem &&
    currentItem.kind === 'image' &&
    thumbItem.kind === 'image' &&
    currentItem.src === thumbItem.src,
  )
}

watch(
  () => displayList.value.length,
  async () => {
    if (currentIndex.value >= displayList.value.length) {
      currentIndex.value = 0
    }

    await syncThumbVisibility()
  },
)

watch(
  () => props.info,
  async () => {
    currentIndex.value = 0
    await syncThumbVisibility()
  },
  { deep: true },
)
</script>

<template>
  <div class="survey-swiper-com w-full">
    <div class="mx-auto flex w-full flex-col gap-[20px]">
      <div class="group relative h-[376px] w-full overflow-hidden rounded-[12px] bg-[#f5f7fb]">
        <template v-if="bigImageSrc">
          <CommonImage
            :src="bigImageSrc"
            class="h-full w-full object-cover"
            :alt="props.info?.title || ''"
            loading="eager"
          />
        </template>
        <div v-else class="h-full w-full bg-[#dfe6ef]"></div>

        <button
          v-if="currentDisplayItem?.kind === 'vr'"
          type="button"
          class="absolute top-1/2 left-1/2 z-[4] flex -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center"
          aria-label="open vr dialog"
          @click="openVrDialog"
        >
          <CommonImage class="h-[48px] w-[76px]" :src="VR_ENTRY_ICON" alt="vr" loading="eager" />
        </button>

        <button
          v-if="currentDisplayItem?.kind === 'video'"
          type="button"
          class="absolute top-1/2 left-1/2 z-[4] flex -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center"
          aria-label="open video dialog"
          @click="openVideoDialog"
        >
          <CommonImage :src="VIDEO_ENTRY_ICON" alt="video" class="size-[60px]" loading="eager" />
        </button>

        <button
          v-if="displayList.length > 1"
          type="button"
          class="pointer-events-none absolute top-1/2 right-auto left-[16px] z-[3] flex h-[40px] w-[40px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-[12px] bg-[rgba(5,12,31,0.46)] text-white opacity-0 transition group-hover:pointer-events-auto group-hover:opacity-100 hover:bg-[rgba(5,12,31,0.62)]"
          aria-label="previous image"
          @click="prevImg"
        >
          <CommonImage class="size-[20px]" :src="LEFT_ICON" alt="icon" loading="eager" />
        </button>

        <button
          v-if="displayList.length > 1"
          type="button"
          class="pointer-events-none absolute top-1/2 right-[16px] left-auto z-[3] flex h-[40px] w-[40px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-[12px] bg-[rgba(5,12,31,0.46)] text-white opacity-0 transition group-hover:pointer-events-auto group-hover:opacity-100 hover:bg-[rgba(5,12,31,0.62)]"
          aria-label="next image"
          @click="nextImg"
        >
          <CommonImage class="size-[20px]" :src="RIGHT_ICON" alt="icon" loading="eager" />
        </button>

        <div
          class="absolute top-[16px] right-[16px] z-[3] flex items-center overflow-hidden rounded-[4px] rtl:right-auto rtl:left-[16px]"
        >
          <button
            v-if="hasVr"
            type="button"
            class="flex h-[30px] cursor-pointer items-center justify-center border-[0.5px] border-r-0 border-solid border-[rgba(11,18,40,0.14)] px-[12px] py-[4px] text-[14px] leading-[22px] font-[500] text-white transition"
            :class="getTopOptionClass('vr')"
            @click="selectKind('vr')"
          >
            VR
          </button>
          <button
            v-if="hasVideo"
            type="button"
            class="flex h-[30px] cursor-pointer items-center justify-center gap-[4px] border-[0.5px] border-r-0 border-solid border-[rgba(11,18,40,0.14)] px-[12px] py-[4px] text-[14px] leading-[22px] font-[500] text-white transition"
            :class="{
              'border-l-0': !hasVr,
              [getTopOptionClass('video')]: true,
            }"
            @click="selectKind('video')"
          >
            <CommonImage :src="TOP_VIDEO_ICON" alt="video" class="h-[16px] w-[16px]" loading="eager" />
            {{ t('84085') }}
          </button>
          <button
            type="button"
            class="flex h-[30px] cursor-pointer items-center justify-center gap-[4px] border-[0.5px] border-solid border-[rgba(11,18,40,0.14)] px-[12px] py-[4px] text-[14px] leading-[22px] font-[500] text-white transition"
            :class="{
              'border-l-0': hasVideo || hasVr,
              [getTopOptionClass('image')]: true,
            }"
            @click="selectImageCount"
          >
            <CommonImage :src="TOP_IMAGE_ICON" alt="picture" class="size-[16px]" loading="eager" />
            <div class="min-w-[24px]">
              {{ currentCount }}
            </div>
          </button>
        </div>
      </div>

      <div class="relative flex h-[86px] w-full items-center gap-[20px] overflow-hidden rtl:flex-row-reverse">
        <button
          v-if="displayList.length > 1"
          type="button"
          class="flex h-[24px] w-[24px] shrink-0 items-center justify-center text-[24px] text-[#1f2430] transition hover:text-[#0f172a]"
          :aria-label="isRtl ? 'next thumbnail' : 'previous thumbnail'"
          @click="prevImg"
        >
          <CommonImage class="size-[24px] cursor-pointer" :src="THUMB_LEFT_ICON" alt="icon" loading="eager" />
        </button>

        <div
          class="flex min-w-0 flex-1 gap-[16px] overflow-x-auto overflow-y-hidden scroll-smooth [scrollbar-width:none]"
        >
          <button
            v-for="(src, index) in thumbDisplayList"
            :key="`${src.src}-${src.kind}-${index}`"
            :ref="
              (el: HTMLElement | null) => {
                if (el) {
                  thumbRefs[index] = el as HTMLElement
                }
              }
            "
            type="button"
            class="relative h-[86px] w-[154px] shrink-0 cursor-pointer overflow-hidden rounded-[8px] border-[2px] border-solid transition"
            :class="[isThumbActive(index) ? 'border-[#cfe1fd]' : 'border-[#fff]']"
            :aria-current="isThumbActive(index) ? 'true' : undefined"
            @click="selectThumb(index)"
          >
            <CommonImage
              :src="thumbImageSrc(src.src)"
              class="h-full w-full object-cover"
              :alt="props.info?.title || ''"
              loading="eager"
            />

            <div
              v-if="!isThumbActive(index)"
              class="absolute inset-0 z-[1] flex items-center justify-center rounded-[6px] bg-[rgba(5,12,31,0.46)] hover:bg-[rgba(0,0,0,0.2)] hover:[&>img]:hidden"
              :aria-label="index"
            >
              <CommonImage
                v-if="getThumbMaskKind(index) === 'video'"
                :src="THUMB_VIDEO_ICON"
                alt="video"
                class="h-[24px] w-[24px]"
                loading="eager"
              />

              <CommonImage
                v-else-if="getThumbMaskKind(index) === 'vr'"
                :src="THUMB_VR_ICON"
                alt="vr"
                class="h-[24px] w-[24px]"
                loading="eager"
              />
              <CommonImage v-else :src="THUMB_IMAGE_ICON" alt="picture" class="h-[24px] w-[24px]" loading="eager" />
            </div>
          </button>
        </div>

        <button
          v-if="displayList.length > 1"
          type="button"
          class="flex h-[24px] w-[24px] shrink-0 items-center justify-center text-[24px] text-[#1f2430] transition hover:text-[#0f172a]"
          :aria-label="isRtl ? 'previous thumbnail' : 'next thumbnail'"
          @click="nextImg"
        >
          <CommonImage class="size-[24px] cursor-pointer" :src="THUMB_RIGHT_ICON" alt="" loading="eager" />
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="less">
.survey-swiper-com {
  :deep(.img) {
    width: 100%;
    height: 100%;
  }

  :deep(.iconfont) {
    line-height: 1;
  }

  .flex::-webkit-scrollbar {
    display: none;
  }
}
</style>
